import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ClipboardList, Loader2, Send, MessageSquare, Mail, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import {
  listIntakeInstancesForOpportunity,
  normalizeIntakePhotoUrls,
  getIntakeFormFillProgress,
  resendIntakeInstanceLink,
  getPublicIntakeUrl,
  type IntakeFormInstanceRow,
  type IntakeFieldSnapshot,
  type IntakeInstanceChannel,
} from '../../lib/crmIntakeFormInstances';
import type { IntakeCustomHostFields } from '../../lib/intakePublicUrl';
import { useImageViewer } from '../ImageViewerProvider';
import IntakeSendSmsReviewModal from './IntakeSendSmsReviewModal';
import IntakeSendEmailReviewModal from './IntakeSendEmailReviewModal';

function statusLabel(status: string): string {
  switch (status) {
    case 'sent':
      return 'Sent';
    case 'opened':
      return 'Opened';
    case 'in_progress':
      return 'In progress';
    case 'submitted':
      return 'Submitted';
    default:
      return status;
  }
}

function statusStyle(status: string): string {
  switch (status) {
    case 'submitted':
      return 'bg-green-50 text-green-700';
    case 'in_progress':
      return 'bg-amber-50 text-amber-800';
    case 'opened':
      return 'bg-purple-50 text-purple-700';
    case 'sent':
      return 'bg-blue-50 text-blue-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function formatFieldValue(
  field: IntakeFieldSnapshot,
  responses: Record<string, unknown>,
  onOpenPhoto?: (urls: string[], index: number) => void,
): React.ReactNode {
  const v = responses[field.key];
  if (v === undefined || v === null) return '—';
  if (field.field_type === 'checkbox') {
    if (v === true || v === 'true') return 'Yes';
    if (v === false || v === 'false') return 'No';
    return String(v);
  }
  if (field.field_type === 'photo') {
    const urls = normalizeIntakePhotoUrls(v);
    if (urls.length === 0) return '—';
    return (
      <span className="inline-flex flex-wrap gap-2 justify-end">
        {urls.map((u, i) => (
          <button
            key={u}
            type="button"
            onClick={() => onOpenPhoto?.(urls, i)}
            className="inline-flex shrink-0"
          >
            <img src={u} alt="" className="h-16 w-16 object-cover rounded-lg border border-gray-200 cursor-pointer" />
          </button>
        ))}
      </span>
    );
  }
  return String(v);
}

interface OpportunityIntakeFormsSectionProps {
  opportunityId: number;
  businessId: string;
  /** Increment after a new send so the list refetches immediately (Realtime also updates drafts). */
  refreshNonce?: number;
  /** Shown in resend picker when available; row still loads contact from DB if omitted. */
  customerPhone?: string | null;
  customerEmail?: string | null;
  businessName?: string | null;
  customerFirstName?: string | null;
}

export default function OpportunityIntakeFormsSection({
  opportunityId,
  businessId,
  refreshNonce = 0,
  customerPhone,
  customerEmail,
  businessName,
  customerFirstName,
}: OpportunityIntakeFormsSectionProps) {
  const { open: openImageViewer } = useImageViewer();
  const [instances, setInstances] = useState<IntakeFormInstanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [resendPickerInstance, setResendPickerInstance] = useState<IntakeFormInstanceRow | null>(null);
  const [resendPickerContact, setResendPickerContact] = useState<{ phone: string; email: string } | null>(null);
  const [resendCompose, setResendCompose] = useState<{
    inst: IntakeFormInstanceRow;
    mode: IntakeInstanceChannel;
  } | null>(null);
  const [intakeHostRow, setIntakeHostRow] = useState<IntakeCustomHostFields | null>(null);

  const load = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    try {
      const rows = await listIntakeInstancesForOpportunity(opportunityId);
      setInstances(rows);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    void load();
  }, [load, businessId, refreshNonce]);

  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    void supabase
      .from('businesses')
      .select('intake_custom_hostname, intake_custom_host_verified_at')
      .eq('id', businessId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled || error) return;
        setIntakeHostRow((data as IntakeCustomHostFields) || null);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, refreshNonce]);

  useEffect(() => {
    if (!supabase) return;

    const channel = supabase
      .channel(`crm_intake_instances_opp_${opportunityId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_intake_form_instances',
          filter: `opportunity_id=eq.${opportunityId}`,
        },
        () => {
          void load();
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [opportunityId, load]);

  useEffect(() => {
    if (!resendPickerInstance || !supabase) {
      setResendPickerContact(null);
      return;
    }
    let cancelled = false;
    void supabase
      .from('customers')
      .select('phone, email')
      .eq('id', resendPickerInstance.customer_id)
      .eq('business_id', businessId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled || error) return;
        setResendPickerContact({
          phone: (data?.phone || '').trim(),
          email: (data?.email || '').trim(),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [resendPickerInstance, businessId]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const phoneForPicker = (customerPhone || '').trim() || (resendPickerContact?.phone || '').trim();
  const emailForPicker = (customerEmail || '').trim() || (resendPickerContact?.email || '').trim();
  const phoneOk = Boolean(phoneForPicker);
  const emailOk = Boolean(emailForPicker);
  const bizName = (businessName || '').trim() || 'Our team';
  const firstName = (customerFirstName || '').trim() || 'there';

  const openResendCompose = (inst: IntakeFormInstanceRow, channel: IntakeInstanceChannel) => {
    if (channel === 'sms' && !phoneOk) {
      toast.error('Customer has no phone number on file');
      return;
    }
    if (channel === 'email' && !emailOk) {
      toast.error('Customer has no email on file');
      return;
    }
    setResendPickerInstance(null);
    setResendCompose({ inst, mode: channel });
  };

  const handleResendSmsSend = async (message: string) => {
    if (!resendCompose || resendCompose.mode !== 'sms') return;
    const inst = resendCompose.inst;
    try {
      await resendIntakeInstanceLink({
        businessId,
        opportunityId,
        customerId: inst.customer_id,
        channel: 'sms',
        accessToken: String(inst.access_token),
        instanceId: inst.id,
        titleSnapshot: inst.title_snapshot,
        smsBody: message,
      });
      toast.success('Form link resent by SMS');
      setResendCompose(null);
      void load();
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Could not resend link');
    }
  };

  const handleResendEmailSend = async (subject: string, bodyPlain: string) => {
    if (!resendCompose || resendCompose.mode !== 'email') return;
    const inst = resendCompose.inst;
    try {
      await resendIntakeInstanceLink({
        businessId,
        opportunityId,
        customerId: inst.customer_id,
        channel: 'email',
        accessToken: String(inst.access_token),
        instanceId: inst.id,
        titleSnapshot: inst.title_snapshot,
        emailSubject: subject,
        emailBodyPlain: bodyPlain,
      });
      toast.success('Form link resent by email');
      setResendCompose(null);
      void load();
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Could not resend link');
    }
  };

  if (loading && instances.length === 0) {
    return (
      <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] px-4 py-3 flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
        Loading intake forms…
      </div>
    );
  }

  if (instances.length === 0) return null;

  const resendChannelModal =
    resendPickerInstance &&
    createPortal(
      <div className="fixed inset-0 z-[20020] flex items-end sm:items-center justify-center sm:p-4">
        <button
          type="button"
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setResendPickerInstance(null)}
          aria-label="Close"
        />
        <div
          className="relative w-full sm:max-w-md bg-white sm:rounded-2xl shadow-2xl border border-gray-200 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Send className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-gray-900">Resend form link</h2>
                <p className="text-xs text-gray-500 truncate">Same link — choose how to send it</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setResendPickerInstance(null)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="px-4 py-4 space-y-2">
            <p className="text-sm font-medium text-gray-900 truncate">{resendPickerInstance.title_snapshot}</p>
            <button
              type="button"
              disabled={!phoneOk}
              onClick={() => openResendCompose(resendPickerInstance, 'sms')}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-left"
            >
              <MessageSquare className="h-5 w-5 text-green-600 flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold text-gray-900">Send via SMS</div>
                <div className="text-xs text-gray-500">{phoneOk ? phoneForPicker : 'No phone on file'}</div>
              </div>
            </button>
            <button
              type="button"
              disabled={!emailOk}
              onClick={() => openResendCompose(resendPickerInstance, 'email')}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-left"
            >
              <Mail className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Send via email</div>
                <div className="text-xs text-gray-500 truncate">{emailOk ? emailForPicker : 'No email on file'}</div>
              </div>
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );

  const resendIntakeUrl = resendCompose
    ? getPublicIntakeUrl(String(resendCompose.inst.access_token), intakeHostRow)
    : '';

  return (
    <>
      {resendChannelModal}
      {resendCompose?.mode === 'sms' && phoneOk && (
        <IntakeSendSmsReviewModal
          isOpen
          onDismiss={() => setResendCompose(null)}
          onSend={handleResendSmsSend}
          customerPhone={phoneForPicker}
          customerFirstName={firstName}
          businessName={bizName}
          intakeUrl={resendIntakeUrl}
          formTitle={resendCompose.inst.title_snapshot}
        />
      )}
      {resendCompose?.mode === 'email' && emailOk && (
        <IntakeSendEmailReviewModal
          isOpen
          onDismiss={() => setResendCompose(null)}
          onSend={handleResendEmailSend}
          customerEmail={emailForPicker}
          customerFirstName={firstName}
          businessName={bizName}
          intakeUrl={resendIntakeUrl}
          formTitle={resendCompose.inst.title_snapshot}
        />
      )}
      <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50/80">
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Live intake forms</div>
        </div>
        <div className="divide-y divide-gray-100">
          {instances.map((inst) => {
            const isOpen = expanded.has(inst.id);
            const fields = Array.isArray(inst.fields_snapshot) ? inst.fields_snapshot : [];
            const responses = inst.responses as Record<string, unknown>;
            const { filled, total } = getIntakeFormFillProgress(fields, responses);
            const progressLabel = total === 0 ? 'No fields on form' : `${filled}/${total} fields filled`;
            return (
              <div key={inst.id} className="bg-white">
                <div className="flex items-stretch">
                  <button
                    type="button"
                    onClick={() => toggle(inst.id)}
                    aria-expanded={isOpen}
                    aria-label={`${inst.title_snapshot}, ${statusLabel(inst.status)}, ${progressLabel}`}
                    className="flex-1 min-w-0 px-4 py-3 flex items-center gap-3 text-left active:bg-gray-50 transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{inst.title_snapshot}</div>
                      <div className="text-xs text-gray-500 mt-0.5 tabular-nums">{progressLabel}</div>
                      <span
                        className={`inline-block text-[10px] font-medium mt-1 px-1.5 py-px rounded-full ${statusStyle(inst.status)}`}
                      >
                        {statusLabel(inst.status)}
                      </span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <div className="flex items-center border-l border-gray-100 bg-white pr-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setResendPickerInstance(inst);
                      }}
                      disabled={Boolean(resendPickerInstance) || Boolean(resendCompose)}
                      title="Resend the same link (choose SMS or email)"
                      className="flex flex-col items-center justify-center min-w-[4.5rem] px-2 py-2 text-[11px] font-semibold text-blue-600 hover:bg-blue-50 active:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Send className="h-4 w-4 text-blue-500 mb-0.5" />
                      Resend
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="px-4 pb-3 pt-0 space-y-2 border-t border-gray-50">
                    {fields.length === 0 ? (
                      <p className="text-xs text-gray-400 py-2">No fields on this form.</p>
                    ) : (
                      (() => {
                        const sorted = [...fields].sort((a, b) => {
                          const so = (a.section_order ?? 0) - (b.section_order ?? 0);
                          if (so !== 0) return so;
                          return (a.field_order ?? 0) - (b.field_order ?? 0);
                        });
                        return sorted.map((f, fi) => {
                          const prev = fi > 0 ? sorted[fi - 1] : null;
                          const showSec = f.section_title && (!prev || prev.section_id !== f.section_id);
                          return (
                            <React.Fragment key={f.key}>
                              {showSec && (
                                <div
                                  className={
                                    fi === 0
                                      ? 'pt-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800'
                                      : 'pt-3 mt-1 border-t border-gray-100 text-[11px] font-semibold uppercase tracking-wide text-emerald-800'
                                  }
                                >
                                  {f.section_title}
                                </div>
                              )}
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-0.5 py-2 border-b border-gray-50 last:border-0">
                                <span className="text-xs font-medium text-gray-500 sm:w-[40%]">{f.label}</span>
                                <span className="text-sm text-gray-900 sm:text-right sm:flex-1 break-words">
                                  {formatFieldValue(f, inst.responses as Record<string, unknown>, (urls, idx) =>
                                    openImageViewer({ images: urls.map((u) => ({ src: u })), index: idx })
                                  )}
                                </span>
                              </div>
                            </React.Fragment>
                          );
                        });
                      })()
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
